import { ReactElement, cloneElement, useState } from "react";
import { makeStyles } from "tss-react/mui";
import { POPPER_DELAY } from "@miplatform/constants";
import {
  IHome,
  IMenuItemConfig,
  IStatusDisplayConfig,
  PopperPlacement,
} from "@miplatform/types";
import { formatDate } from "@miplatform/utils";
import { Box, Typography } from "@mui/material";
import { MiMenu } from "../MiMenu/MiMenu";
import { MiPopper } from "../MiPopper/MiPopper";
import { WorkCardHeader } from "./WorkCardHeader";
import { WorkCardStatus } from "./WorkCardStatus";

export const MiWorkCard = ({
  title,
  displayDate,
  status,
  home,
  statusConfig,
  statusBottomText,
  headerColor,
  headerIcon,
  menuItems,
  isRequestedInDateRange,
  isInBucket,
  popperComponent,
  popperPlacement = PopperPlacement.Auto,
}: {
  title: string;
  displayDate?: string;
  status: string;
  home: IHome;
  statusConfig: Record<string, IStatusDisplayConfig>;
  statusBottomText?: string;
  headerColor?: string;
  headerIcon?: { path: string; size?: number; color?: string } | null;
  menuItems?: IMenuItemConfig[];
  isRequestedInDateRange?: boolean;
  isInBucket?: boolean;
  popperComponent?: React.ReactNode;
  popperPlacement?: PopperPlacement;
}) => {
  const { cardBackgroundColor, cardBorderColor } = statusConfig[status] ?? {};
  const { classes } = useStyles({
    backgroundColor: cardBackgroundColor,
    borderColor: cardBorderColor,
    isInBucket,
    isRequestedInDateRange,
  });
  const [popperOpen, setPopperOpen] = useState(false);
  const [contextMenuOpen, setContextMenuOpen] = useState(false);
  const [timeoutId, setTimeoutId] = useState<number | undefined>(undefined);
  const [contextMenuPosition, setContextMenuPosition] = useState<{
    left: number;
    top: number;
  } | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleOpenContextMenu = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();

    if (contextMenuOpen) {
      handleCloseContextMenu();
      return;
    }

    setPopperOpen(false);
    setAnchorEl(e.currentTarget);
    setContextMenuPosition(
      !contextMenuPosition ? { left: e.clientX + 2, top: e.clientY - 6 } : null
    );
    setContextMenuOpen(true);
  };

  const handleCloseContextMenu = () => {
    setContextMenuOpen(false);
    setContextMenuPosition(null);
    setAnchorEl(null);
  };

  const openPopper = (e: React.MouseEvent<HTMLDivElement>) => {
    if (contextMenuOpen) {
      return;
    }
    const { currentTarget } = e;
    const timeout = setTimeout(() => {
      setPopperOpen(true);
      setAnchorEl(currentTarget);
    }, POPPER_DELAY);
    setTimeoutId(timeout);
  };

  const closePopper = () => {
    clearTimeout(timeoutId);
    setAnchorEl(null);
    setPopperOpen(false);
  };

  const {
    address: { addressLine1 = "No Home Found.", city = null } = {},
    communityName = null,
  } = home ?? {};

  return (
    <Box
      className={classes.cardContainer}
      onContextMenu={handleOpenContextMenu}
      onMouseEnter={openPopper}
      onMouseLeave={closePopper}
    >
      <Box>
        <WorkCardHeader
          title={title}
          backgroundColor={headerColor}
          icon={headerIcon}
          isRequestedInDateRange={isRequestedInDateRange}
          isInBucket={isInBucket}
        />
      </Box>
      <Box className={classes.cardContent}>
        <Box className={classes.statusContainer}>
          <WorkCardStatus
            config={statusConfig}
            status={status}
            date={displayDate ? formatDate(displayDate, "M/D/YY") : ""}
            bottomText={statusBottomText}
          />
        </Box>
        <Box className={classes.cardInfo} flex={1}>
          <Box
            className={classes.cardInfoLine}
            display="flex"
            flex={1}
            justifyContent="space-between"
            gap={0.5}
          >
            <Typography
              className={`${classes.cardText} ${classes.addressLine}`}
              flex={2}
              flexShrink={1}
            >
              {addressLine1}
            </Typography>
            {city && (
              <Typography
                className={`${classes.cardText} ${classes.detailsText}`}
                flex={1}
                flexShrink={3}
                textAlign="right"
                paddingRight={1} // to avoid the italics getting cut off
              >
                {city}
              </Typography>
            )}
          </Box>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            gap={0.5}
          >
            <Typography
              className={`${classes.cardText} ${classes.detailsText}`}
              flex={2}
            >
              {communityName}
            </Typography>
          </Box>
        </Box>
        {menuItems && (
          <MiMenu
            data={menuItems}
            menuStyles={{ zIndex: 2000 }}
            optionalElement={<span />}
            closeContextMenu={handleCloseContextMenu}
            open={contextMenuOpen}
            anchorPosition={contextMenuPosition}
          />
        )}
        {popperComponent && (
          <MiPopper
            anchorEl={anchorEl}
            arrowColor={headerColor ?? "white"}
            showArrow
            open={popperOpen}
            placement={popperPlacement}
          >
            {cloneElement(popperComponent as ReactElement<any>, {
              onClose: closePopper,
            })}
          </MiPopper>
        )}
      </Box>
    </Box>
  );
};

const useStyles = makeStyles<{
  backgroundColor?: string;
  borderColor?: string;
  isInBucket?: boolean;
  isRequestedInDateRange?: boolean;
}>()(
  (
    theme,
    { backgroundColor, borderColor, isInBucket, isRequestedInDateRange }
  ) => ({
    cardContainer: {
      display: "flex",
      flexDirection: "column",
      backgroundColor: backgroundColor ?? theme.palette.common.white,
      width: "100%",
      border: `1px solid ${theme.palette.grey[600]}`,
      borderColor: borderColor ?? theme.palette.grey[600],
      borderRadius: theme.spacing(1),
      overflow: "hidden",
      height: "100%",
      ...(!isRequestedInDateRange &&
        isInBucket && {
          backgroundImage: `repeating-linear-gradient(135deg, rgba(0, 0, 0, 0.12) ${
            0 + 2.6251
          }px, rgba(0, 0, 0, 0.12) ${2 + 2.6251}px, transparent ${
            2 + 2.6251
          }px, transparent ${6 + 2.6251}px)`,
        }),
    },
    cardContent: {
      display: "flex",
    },
    statusContainer: {
      width: 56,
      height: 40,
    },
    cardInfo: {
      display: "flex",
      flexDirection: "column",
      textOverflow: "ellipsis",
      minWidth: 0,
      padding: theme.spacing(0, 0.5),
    },
    cardInfoLine: {
      width: "100%",
      textOverflow: "ellipsis",
      minWidth: 0,
    },
    cardText: {
      fontSize: "0.75rem",
      textWrap: "nowrap",
      minWidth: 0,
      overflow: "hidden",
    },
    addressLine: {
      color: theme.palette.grey[900],
    },
    detailsText: {
      color: theme.palette.grey[600],
      fontStyle: "italic",
    },
  })
);
